import { Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { selectImpactCalculateTourCo2ForProject } from '@/domain/impact-calculate-tour/impact-calculate-tour.selectors';
import { ProjectTour } from '@/domain/project-tour/entities/project-tour.entity';
import { FSCard } from '@/ui/components/FSCard';
import { ScoreTag } from '@/ui/components/score/ScoreTag';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { ShowDetailsMode } from '@/ui/pages/app/components/project-page/ProjectScores';
import { ProjectScoresDetailsItemParams } from '@/ui/pages/app/components/project-page/ProjectScoresDetails/ProjectScoresDetails.types';
import { ColorsRaw } from '@/ui/theme/colors-raw';
import { ColorsScore } from '@/ui/theme/colors-score';

import { Co2OnTourDetails } from './co2-ontour-details/Co2OnTourDetails';
import { ProjectTourScoresDetails } from './ProjectToursScoresDetails';

export function ProjectTourScoresDetailsCo2Custom({
  project,
  showDetailsMode,
  onClickDetailsItem,
  onClickOpenOnTourSequence,
}: {
  project: ProjectTour;
  showDetailsMode: ShowDetailsMode;
  onClickDetailsItem: (params: ProjectScoresDetailsItemParams) => void;
  onClickOpenOnTourSequence: (sequenceId: number) => void;
}) {
  const [co2SubMode, setCo2SubMode] = useState<Co2SubMode>('general');

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // detect if submode is set in url params
    const urlParamSubMode = searchParams.get('submode');
    if (Co2SubModesValues.includes(urlParamSubMode as Co2SubMode)) {
      setCo2SubMode(urlParamSubMode as Co2SubMode);
    }
    // remove submode in url params
    searchParams.delete('submode');
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const calculateCo2 = useAppSelector(
    selectImpactCalculateTourCo2ForProject(project.id),
  );
  const co2General = calculateCo2.general.score;
  const co2OnTour = calculateCo2.ontour.score;

  const onToggleButtonChange = (newValue: Co2SubMode | null) => {
    if (newValue !== null) {
      setCo2SubMode(newValue);
    }
  };

  return (
    <>
      <Stack sx={{ gap: 3 }}>
        <Stack direction={'row'} spacing={3} alignSelf={'center'}>
          <Co2SubModeButton
            title={translate('ProjectsTour.project-tour-general')}
            co2={co2General}
            selected={co2SubMode === 'general'}
            onClick={() => onToggleButtonChange('general')}
          />
          <Co2SubModeButton
            title={translate('ProjectsTour.project-tour-ontour')}
            co2={co2OnTour}
            selected={co2SubMode === 'ontour'}
            onClick={() => onToggleButtonChange('ontour')}
          />
        </Stack>
        {co2SubMode === 'general' && (
          <ProjectTourScoresDetails
            project={project}
            showDetailsMode={showDetailsMode}
            onClickDetailsItem={onClickDetailsItem}
          />
        )}
        {co2SubMode === 'ontour' && (
          <Co2OnTourDetails
            onClickOpenOnTourSequence={onClickOpenOnTourSequence}
          />
        )}
      </Stack>
    </>
  );
}

const Co2SubModesValues = ['general', 'ontour'] as const;
type Co2SubMode = (typeof Co2SubModesValues)[number];

function Co2SubModeButton({
  title,
  co2,
  onClick,
  selected,
}: {
  title: string;
  co2: number;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <FSCard
      onClick={onClick}
      sx={{
        width: '15rem',
        backgroundColor: selected
          ? ColorsRaw.anthracite.Warm_Anthracite_2
          : '#160707',
      }}
    >
      <Stack sx={{ gap: 2, padding: 3 }}>
        <Typography color="text.secondary" sx={{ fontSize: 20 }}>
          {title}
        </Typography>
        <ScoreTag value={co2} colors={ColorsScore.none} topic="co2" />
      </Stack>
    </FSCard>
  );
}
